import { useEffect, useReducer, useRef, useState } from 'react';
import { EMPTY_SNAPSHOT, RoomEvent, RoomSnapshot, reduce } from './types';

export interface UseRoomStreamOpts {
  apiBase: string;          // e.g. https://api.example.com
  roomId:  string;
  token:   string;          // spectator JWT (?token=...)
  // For tests: inject an EventSource constructor.
  EventSourceCtor?: typeof EventSource;
}

export interface UseRoomStreamResult {
  snapshot: RoomSnapshot;
  connected: boolean;
  error: string | null;
  /** Last event-id seen (for reconnect-replay). */
  lastSeq: number;
}

function reducer(state: RoomSnapshot, event: RoomEvent): RoomSnapshot {
  return reduce(state, event);
}

export function useRoomStream(opts: UseRoomStreamOpts): UseRoomStreamResult {
  const [snapshot, dispatch] = useReducer(reducer, { ...EMPTY_SNAPSHOT, roomId: opts.roomId });
  const [connected, setConnected] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const lastSeq = useRef(0);

  useEffect(() => {
    if (!opts.roomId || !opts.token) return;
    const Source = opts.EventSourceCtor ?? EventSource;

    let cancelled = false;
    let backoff = 500;
    let es: EventSource | null = null;

    const connect = () => {
      if (cancelled) return;
      const url = `${opts.apiBase}/rooms/${opts.roomId}/stream`
        + `?token=${encodeURIComponent(opts.token)}`
        + (lastSeq.current ? `&since=${lastSeq.current}` : '');
      try {
        es = new Source(url, { withCredentials: false });
      } catch (e) {
        setError((e as Error).message);
        return;
      }
      es.onopen = () => {
        setConnected(true);
        setError(null);
        backoff = 500;
      };
      es.onmessage = (msg: MessageEvent) => {
        try {
          const payload = JSON.parse(msg.data) as RoomEvent;
          dispatch(payload);
          if (msg.lastEventId) {
            const n = parseInt(msg.lastEventId, 10);
            if (Number.isFinite(n)) lastSeq.current = n;
          }
        } catch (err) {
          // Drop malformed events; keep the stream going.
          console.warn('drop malformed event', err);
        }
      };
      es.onerror = () => {
        setConnected(false);
        es?.close();
        if (cancelled) return;
        setError('Disconnected — retrying');
        setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, 10_000);
      };
    };

    connect();
    return () => {
      cancelled = true;
      es?.close();
    };
  }, [opts.apiBase, opts.roomId, opts.token, opts.EventSourceCtor]);

  return { snapshot, connected, error, lastSeq: lastSeq.current };
}
